import React, { useEffect, useRef, useState } from 'react';
import interact from 'interactjs';
import { Stack, ToggleButton, ToggleButtonGroup, Typography, useMediaQuery, Theme, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { FormatAlignCenter, FormatAlignJustify, FormatAlignLeft, FormatAlignRight, FormatBold, FormatClear, FormatListBulleted, FormatListNumbered, FormatStrikethrough, FormatUnderlined } from '@mui/icons-material';

interface TextEditorProps {
  value: string;
  disabled?: boolean;
  onChange: (value: string) => void;
}

const TextEditor: React.FC<TextEditorProps> = ({ value, disabled, onChange }) => {
  const started = useRef(false);
  const editorRef = useRef<HTMLDivElement>(null);
  const [editable, setEditable] = useState(true);
  const showToolbar = useMediaQuery('(min-width:500px)');
  const [selectedFontSize, setSelectedFontSize] = useState<string>('');

  function handleDragStart(e: DragEvent) {
    const selection = window.getSelection();
    if (!selection || !selection.rangeCount) return false;

    let html = "";
    let plain = "";

    for (const range of selection.getRangeAt(0).cloneContents().childNodes) {
      if (range.nodeType === Node.TEXT_NODE) {
        plain += range.textContent;
        html += range.textContent;
      } else if (range.nodeType === Node.ELEMENT_NODE) {
        const div = document.createElement('div');
        div.appendChild(range.cloneNode(true));
        html += div.innerHTML;
      }
    }

    e.dataTransfer?.setData('text/html', html);
    e.dataTransfer?.setData('text/plain', plain);
  }

  useEffect(() => {
    const styleId = 'editor-scrollbar-style';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .editor::-webkit-scrollbar {
          display: none;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  useEffect(() => {
    if (editorRef.current && !started.current) {
      editorRef.current.innerHTML = value;
      editorRef.current.addEventListener('dragstart', handleDragStart, false);
      editorRef.current.addEventListener('dragover', function(e) {
      }, false);
    }
  }, []);

  useEffect(() => {
    if (disabled) {
      return;
    }

    interact('.editor img')
      .resizable({
        edges: { left: true, right: true, bottom: true, top: true },
        modifiers: [
          interact.modifiers.restrictEdges({
            outer: 'parent',
            endOnly: true,
          }),
          interact.modifiers.restrictSize({
            min: { width: 100, height: 50 },
          }),
        ],
        listeners: {
          start: () => {
            setEditable(false);
            editorRef.current?.blur()
          },
          end: () => {
            setEditable(true);
            handleInput(new Event('input') as any as React.FormEvent<HTMLDivElement>);
          }
        },
        inertia: true,
      })
      .on('resizemove', function (event) {
        let target = event.target;
        let x = (parseFloat(target.getAttribute('data-x')) || 0);
        let y = (parseFloat(target.getAttribute('data-y')) || 0);

        target.style.width = event.rect.width + 'px';
        target.style.height = event.rect.height + 'px';

        x += event.deltaRect.left;
        y += event.deltaRect.top;

        target.style.webkitTransform = target.style.transform =
          'translate(' + x + 'px,' + y + 'px)';

        target.setAttribute('data-x', x);
        target.setAttribute('data-y', y);
      });
  }, [disabled]);

  const handleInput = (e: React.FormEvent<HTMLDivElement>) => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const executeCommand = (command: string, arg?: string) => {
    document.execCommand(command, false, arg);
    handleInput(new Event('input') as any as React.FormEvent<HTMLDivElement>);
  };

  const handleFontSizeChange = (size: string) => {
    setSelectedFontSize(size);

    const numeric = parseInt(size, 10);
    let bucket = '3';
    if (!isNaN(numeric)) {
      if (numeric <= 9) bucket = '1';
      else if (numeric <= 11) bucket = '2';
      else if (numeric <= 13) bucket = '3';
      else if (numeric <= 17) bucket = '4';
      else if (numeric <= 23) bucket = '5';
      else if (numeric <= 35) bucket = '6';
      else bucket = '7';
    }

    executeCommand('fontSize', bucket);
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const selection = window.getSelection();
    selection?.deleteFromDocument();

    const dataHtml = e.dataTransfer.getData('text/html');

    const range = document.caretRangeFromPoint(e.clientX, e.clientY);

    if (!range) {
      return;
    }

    if (e.dataTransfer.files.length) {
      await Promise.all(Array.from(e.dataTransfer.files).map(file => {
        return new Promise(resolve => {
          if (file.type.match('image.*')) {
            const reader = new FileReader();
  
            reader.onload = (readerEvent: ProgressEvent<FileReader>) => {
              const content = readerEvent.target?.result;
  
              if (content && editorRef.current) {
                const img = document.createElement('img');
                img.src = content.toString();
                img.style.maxWidth = '100%';
                img.style.height = 'auto';
                
                range.insertNode(img);
                resolve(void 0);
              }
            };
  
            reader.readAsDataURL(file);
          }
        })
      }));

      handleInput(new Event('input') as any as React.FormEvent<HTMLDivElement>);
    } else {
      range.deleteContents();

      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = dataHtml;
      const frag = document.createDocumentFragment();

      while (tempDiv.firstChild) {
        frag.appendChild(tempDiv.firstChild);
      }

      range.insertNode(frag);
      handleInput(new Event('input') as any as React.FormEvent<HTMLDivElement>);
    }
  };

  return (
    <Stack spacing={2}>
      {!disabled && showToolbar && (
        <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
          <ToggleButtonGroup size="small">
            <ToggleButton value={false} onClick={() => executeCommand('bold')}><FormatBold /></ToggleButton>
            <ToggleButton value={false} onClick={() => executeCommand('underline')}><FormatUnderlined /></ToggleButton>
            <ToggleButton value={false} onClick={() => executeCommand('strikethrough')}><FormatStrikethrough /></ToggleButton>
            <ToggleButton value={false} onClick={() => executeCommand('insertUnorderedList')}><FormatListBulleted /></ToggleButton>
            <ToggleButton value={false} onClick={() => executeCommand('insertOrderedList')}><FormatListNumbered /></ToggleButton>
            <ToggleButton value={false} onClick={() => executeCommand('formatBlock', '<h1>')}><Typography fontWeight="bold">H1</Typography></ToggleButton>
            <ToggleButton value={false} onClick={() => executeCommand('formatBlock', '<h2>')}><Typography fontWeight="bold">H2</Typography></ToggleButton>
            <ToggleButton value={false} onClick={() => executeCommand('justifyLeft')}><FormatAlignLeft /></ToggleButton>
            <ToggleButton value={false} onClick={() => executeCommand('justifyCenter')}><FormatAlignCenter /></ToggleButton>
            <ToggleButton value={false} onClick={() => executeCommand('justifyRight')}><FormatAlignRight /></ToggleButton>
            <ToggleButton value={false} onClick={() => executeCommand('justifyFull')}><FormatAlignJustify /></ToggleButton>
            <ToggleButton value={false} onClick={() => executeCommand('removeFormat')}><FormatClear /></ToggleButton>
          </ToggleButtonGroup>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Размер</InputLabel>
            <Select
              label="Размер"
              value={selectedFontSize}
              onChange={(e) => handleFontSizeChange(e.target.value as string)}
            >
              <MenuItem value=""><em>По умолчанию</em></MenuItem>
              <MenuItem value="8">8</MenuItem>
              <MenuItem value="9">9</MenuItem>
              <MenuItem value="10">10</MenuItem>
              <MenuItem value="11">11</MenuItem>
              <MenuItem value="12">12</MenuItem>
              <MenuItem value="14">14</MenuItem>
              <MenuItem value="16">16</MenuItem>
              <MenuItem value="18">18</MenuItem>
              <MenuItem value="20">20</MenuItem>
              <MenuItem value="24">24</MenuItem>
              <MenuItem value="28">28</MenuItem>
              <MenuItem value="36">36</MenuItem>
              <MenuItem value="48">48</MenuItem>
              <MenuItem value="72">72</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      )}

      <div
        ref={editorRef}
        className="editor"
        contentEditable={!disabled && editable}
        style={{ 
          border: '1px solid #126DF736', 
          borderRadius: '10px', 
          minHeight: '200px', 
          maxHeight: '600px',
          padding: '10px',
          overflow: 'auto',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }}
        onDrop={handleDrop}
        onInput={() => {
          handleInput(new Event('input') as any as React.FormEvent<HTMLDivElement>)
        }}
        onPaste={(e) => {
          setTimeout(() => {
            if (editorRef.current) {
              editorRef.current.scrollTop = editorRef.current.scrollHeight;
            }
          }, 0);
        }}
      />
    </Stack>
);
};

export default TextEditor;
